import { Boxes } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAppContext } from '@/context/AppContext';
import { getTilesetCapabilities } from '@/queries/data-uploads/get-tileset-capabilities';

import { useAssetStructure } from './Add3DDataFields';
import { CesiumId } from './CesiumId';
import { UploadGlbData } from './UploadGlbData';

type Source = 'upload' | 'cesium';

export type Add3DDataDialogProps = {
	onAdd?: () => void;
	assetId?: string;
	inspectionId?: string;
	damageId?: string;
};

/**
 * Single entry point for adding 3D data to an asset. The two sources differ only
 * in how the model gets there — a file the tile server converts, or a tileset
 * that already lives in a Cesium ION account — so they share one dialog, one
 * display name and one asset-structure selection, and are picked with tabs.
 */
export function Add3DDataDialog({ onAdd, assetId, inspectionId, damageId }: Add3DDataDialogProps) {
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);
	const [source, setSource] = useState<Source>('upload');
	const [name, setName] = useState('');
	// Whether this backend can take a file at all (converter or tile server). Until
	// the answer arrives the tab is shown; an older backend has no flag and stays on.
	const [uploadEnabled, setUploadEnabled] = useState(true);
	const [selectedAssetStructureId, setSelectedAssetStructureId] = useState('');
	const { selectedAssetId: assetIdFromContext } = useAppContext();

	const assetStructure = useAssetStructure(assetId, selectedAssetStructureId, setSelectedAssetStructureId);

	useEffect(() => {
		if (open && assetIdFromContext) {
			setSelectedAssetStructureId(assetIdFromContext);
		}
	}, [open, assetIdFromContext]);

	useEffect(() => {
		let alive = true;
		void getTilesetCapabilities().then((capabilities) => {
			if (alive && capabilities?.enabled === false) {
				setUploadEnabled(false);
				setSource('cesium');
			}
		});
		return () => {
			alive = false;
		};
	}, []);

	// Closing the dialog unmounts both panels, so whatever they hold locally (the
	// picked file, the Cesium ID) clears itself. Only the fields hoisted up here
	// have to be reset by hand.
	useEffect(() => {
		if (!open) {
			setName('');
			setSource(uploadEnabled ? 'upload' : 'cesium');
		}
	}, [open, uploadEnabled]);

	const panelProps = {
		assetId,
		inspectionId,
		damageId,
		onAdd,
		onClose: () => setOpen(false),
		name,
		setName,
		assetStructure,
	};

	return (
		<Dialog open={open} onOpenChange={setOpen}>
			<DialogTrigger asChild>
				<Button variant="secondary" size="sm" className="h-7 lg:h-9 text-xs lg:text-sm px-2 lg:px-3">
					<Boxes className="size-3.5 lg:size-4 mr-1" />
					{t('files.upload.dialogTitle')}
				</Button>
			</DialogTrigger>
			<DialogContent className="max-w-md w-full">
				<DialogHeader className="flex flex-col space-y-1.5 text-center sm:text-left">
					<DialogTitle className="text-lg font-semibold leading-none tracking-tight">
						{t('files.upload.dialogTitle')}
					</DialogTitle>
					<DialogDescription>
						{source === 'upload'
							? t('files.upload.uploadDialogDescription')
							: t('files.upload.cesiumDialogDescription')}
					</DialogDescription>
				</DialogHeader>

				<Tabs value={source} onValueChange={(value) => setSource(value as Source)}>
					<TabsList className={uploadEnabled ? 'grid w-full grid-cols-2' : 'grid w-full grid-cols-1'}>
						{uploadEnabled && <TabsTrigger value="upload">{t('files.upload.tabUploadFile')}</TabsTrigger>}
						<TabsTrigger value="cesium">{t('files.upload.tabCesiumIon')}</TabsTrigger>
					</TabsList>
					{uploadEnabled && (
						<TabsContent value="upload">
							<UploadGlbData {...panelProps} />
						</TabsContent>
					)}
					<TabsContent value="cesium">
						<CesiumId {...panelProps} />
					</TabsContent>
				</Tabs>
			</DialogContent>
		</Dialog>
	);
}
